/**
 * src/lib/listCache.js — RC 1.49.4 Tier 1 client-side cache.
 *
 * Module-scoped Map cache with TTL + stale-while-revalidate
 * semantics. Designed to stop the "navigate away, navigate back,
 * watch the entire page skeleton-flash and re-fetch" UX. Used by
 * ApplicationsPage, JobsPage, DashboardPage, and (with a slight
 * variant) by CreditsProvider.
 *
 * Hard rails honoured: zero schema, scoring, governance, prompts,
 * model routing, pricing, Stripe, or backend changes. Pure
 * frontend rendering layer.
 *
 * Design:
 *   • Module-scoped `Map` survives route unmount because it lives
 *     in module scope, not React state. Tab close = cache cleared.
 *   • TTL is per-call (caller picks the right freshness budget).
 *     Default 60 s.
 *   • `stale` mode: when a cache hit is past its TTL, return the
 *     stale value immediately AND fire a background revalidation.
 *     Subscribers get notified when the revalidation completes so
 *     React state can update without a skeleton flash.
 *   • Invalidation is prefix-aware: `invalidate('applications')`
 *     drops every key that starts with `'applications:'`.
 *   • Singleton in-flight promise per key prevents duplicate
 *     fetches when the same page mounts twice in quick succession.
 *
 * No new npm dependency.
 */

/** @typedef {{ data:any, ts:number, ttlMs:number }} Entry */

/** @type {Map<string, Entry>} */
const _store = new Map();

/** @type {Map<string, Promise<any>>} */
const _inflight = new Map();

/** @type {Map<string, Set<(data:any)=>void>>} */
const _subs = new Map();

/** Default freshness window. */
const DEFAULT_TTL_MS = 60 * 1000;

function _now() { return Date.now(); }

function _isFresh(entry, ttlMs) {
  return entry && (_now() - entry.ts) < ttlMs;
}

function _notify(key, data) {
  const set = _subs.get(key);
  if (!set) return;
  for (const cb of set) {
    try { cb(data); } catch { /* swallow — subscriber is responsible */ }
  }
}

/**
 * Read a cache entry without revalidating. Returns:
 *   • { hit:true, fresh:true,  data }    when we have a fresh entry
 *   • { hit:true, fresh:false, data }    when we have a stale entry
 *   • { hit:false }                      when there is nothing
 *
 * Used by callers that want to render synchronously on first paint
 * (e.g. `useState(() => peek(key)?.data ?? null)`).
 *
 * @param {string} key
 * @param {number} [ttlMs=DEFAULT_TTL_MS]
 */
export function peek(key, ttlMs = DEFAULT_TTL_MS) {
  const entry = _store.get(key);
  if (!entry) return { hit: false };
  return { hit: true, fresh: _isFresh(entry, ttlMs), data: entry.data };
}

/**
 * Returns the cached value if fresh; otherwise calls `fetcher()`,
 * stores the result, and resolves with it.
 *
 * Stale-while-revalidate: when `staleAccept` is true and we have a
 * stale entry, the cached value is returned IMMEDIATELY and a
 * background revalidation fires. Subscribers (`subscribe(key, cb)`)
 * are notified when the revalidation lands so React state can be
 * updated silently. Returns the stale data synchronously to the
 * caller; the awaited Promise resolves with the FRESH data once
 * the revalidation completes (or with the stale data if the
 * revalidation throws).
 *
 * @param {string} key
 * @param {() => Promise<any>} fetcher
 * @param {object} [opts]
 * @param {number}  [opts.ttlMs]
 * @param {boolean} [opts.staleAccept=true]   if false, stale entries are ignored
 * @returns {Promise<any>}
 */
export async function cachedFetch(key, fetcher, opts = {}) {
  const ttlMs = opts.ttlMs ?? DEFAULT_TTL_MS;
  const staleAccept = opts.staleAccept !== false;
  const entry = _store.get(key);

  if (_isFresh(entry, ttlMs)) {
    return entry.data;
  }

  // Singleton in-flight: if a fetch is already happening for this
  // key, await it instead of duplicating.
  const pending = _inflight.get(key);
  if (pending) {
    if (staleAccept && entry) return entry.data;   // serve stale while in-flight
    return pending;
  }

  const p = (async () => {
    try {
      const data = await fetcher();
      _store.set(key, { data, ts: _now(), ttlMs });
      _notify(key, data);
      return data;
    } finally {
      _inflight.delete(key);
    }
  })();
  _inflight.set(key, p);

  if (staleAccept && entry) {
    // Stale-while-revalidate: return the stale entry; the awaited
    // promise still resolves with the fresh value, so callers that
    // `await` get the fresh result and callers that subscribe see
    // the silent update.
    p.catch(() => { /* swallow — subscribers/awaiters handle errors */ });
    return entry.data;
  }
  return p;
}

/**
 * Drop one or more cache entries.
 *
 *   invalidate('applications:list:abc')   → drops exactly that key.
 *   invalidate('applications')            → drops every key starting
 *                                            with 'applications:' or
 *                                            equal to 'applications'.
 *   invalidate(['a', 'b', 'c'])           → drops each.
 *
 * Also clears any in-flight promise for the affected keys so a
 * fresh fetch isn't blocked by a now-stale request.
 *
 * @param {string|string[]} keyOrPrefix
 */
export function invalidate(keyOrPrefix) {
  const list = Array.isArray(keyOrPrefix) ? keyOrPrefix : [keyOrPrefix];
  for (const k of list) {
    if (!k) continue;
    if (_store.has(k)) {
      _store.delete(k);
      _inflight.delete(k);
      continue;
    }
    // Prefix sweep
    const prefix = `${k}:`;
    for (const storedKey of Array.from(_store.keys())) {
      if (storedKey.startsWith(prefix) || storedKey === k) {
        _store.delete(storedKey);
        _inflight.delete(storedKey);
      }
    }
  }
}

/**
 * Subscribe to background revalidation completions for `key`.
 * Returns an unsubscribe function. Used by React effects to keep
 * the rendered state in sync with stale-while-revalidate updates
 * without forcing a skeleton flash.
 *
 * @param {string} key
 * @param {(data:any) => void} cb
 * @returns {() => void}
 */
export function subscribe(key, cb) {
  let set = _subs.get(key);
  if (!set) { set = new Set(); _subs.set(key, set); }
  set.add(cb);
  return () => {
    const s = _subs.get(key);
    if (!s) return;
    s.delete(cb);
    if (s.size === 0) _subs.delete(key);
  };
}

/** Test-only — drop everything. NOT exported as a default action. */
export function _resetForTests() {
  _store.clear();
  _inflight.clear();
  _subs.clear();
}

/** Common cache-key builders. Centralised so mutation code paths
 *  can invalidate without typo drift. */
export const KEYS = {
  applicationsList: (qs) => `applications:list:${qs || ''}`,
  applicationPacksAll: () => 'application-packs:all',
  cvVariantsAll:    () => 'cv-variants:all',
  coverLettersAll:  () => 'cover-letters:all',
  jobById:          (id) => `jobs:by-id:${id}`,
  jobsList:         (qs) => `jobs:list:${qs || ''}`,
  dashboardBundle:  () => 'dashboard:bundle',
  accountPlan:      () => 'account:plan',
  analyzeFitUsage:  () => 'usage:analyze-fit',
  // RC 1.59 Slice 2 — Documents tab cache adoption (Tier 1).
  // Module-scoped keys; tab close clears the cache automatically.
  interviewPrepAll: () => 'interview-prep:all',
  sourceCvActive:   (userId) => `source-cv:active:${userId || ''}`,
  // RC 1.59 Slice 3 — JobDetailPage cache adoption (Tier 2). Both
  // are user / job-scoped namespaces so prefix invalidation can
  // sweep the right cohort without overreach.
  uploadedCvs:      (userId) => `uploaded-cvs:${userId || ''}`,
  baselineFit:      (jobId) => `baseline-fit:${jobId || ''}`,
};
